import type { ColumnDef } from '@tanstack/react-table'
import { ArrowLeft, Check, Circle, FilterX, Package, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslate } from 'shared/lib/i18n'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { FacetedFilter } from 'shared/components/FacetedFilter'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import { Input } from 'shared/components/ui/input'
import { TableBody, TableCell, TableRow } from 'shared/components/ui/table'
import { useDataTable } from 'shared/hooks/use-data-table'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from 'shared/components/ui/alert-dialog'
import { ScanInput } from 'shared/components/scan-input'
import { cn } from 'shared/lib/utils'
import { formatDayTime } from 'shared/lib/utils/date'
import { playTone } from 'shared/lib/tone-sound'
import { DamageDialog } from 'shared/components/damage-act'
import { CompactTable, SectionTitle, Stat } from 'shared/components/section'
import { ToneBanner, TONE_CLASS } from 'shared/components/tone-banner'
import { BoxAnswerBanner, BoxForm, BoxesList, CurrentBoxBar, RefusedLinesList, type BoxWeighing } from './HubBoxes'
import { UnreadableLabel, type UnreadableLabelSeams } from './UnreadableLabel'
import {
	boxCallsViaTrpc,
	boxFailure,
	useBoxRefusalWords,
	type BoxBanner,
	type HubBoxCard,
	type HubBoxSeams,
} from '../lib/hub-boxes'

/**
 * Receiving a batch at the hub, per piece — the checklist screen (spec §12, §26
 * view B; ADR-0026, ADR-0004; tickets 25, 55). A hub worker opens a Manifest
 * addressed to the hub and works the physical batch Parcel by Parcel: the scan takes
 * the Parcel in — a Parcel is never weighed (ADR-0047) — and the answer comes back
 * in one of three tones — «готово»
 * (green), «внимание» (yellow: onto the hub shelf, or a repeat scan that wrote
 * nothing), «отказ» (red). The colour is the server's; this screen only shows it.
 *
 * Beside the answer stands the checklist: everything the Manifest announced,
 * ticked as it is taken in, and next to it what is over the list and what went
 * to the hub shelf. Closing is final (ADR-0004): the confirmation names the lines
 * still expected, which close as «не прибыла».
 *
 * Scanners type like a keyboard and press Enter (spec §26): Enter in the scan field
 * sends the scan.
 *
 * The three calls are injected (`load`, `scan`, `close`) so the behaviour is
 * tested without a backend; they default to the real tRPC client.
 *
 * **Receiving by boxes (tickets 11 + 29):** every scan is first identified
 * (`identify`) — a Box opens its own form (`BoxForm`: its weight, optional, then in
 * one press taken in whole or opened; a MIX Box only opened), and a Parcel the
 * server would refuse (inside a Box not opened yet, scanned while no Box is being
 * unpacked) is answered red before anything is written. While a Box is opened its
 * Parcels are scanned with the Box (`boxId`) — one of another Box comes out of it
 * yellow, an extra of it — and «Завершить коробку» compares it. The Boxes and the lines
 * refused when announced are listed beside the checklist; a press on a Box (and
 * opening one) filters the checklist by it.
 *
 * Each answer sounds in its tone, and a stopwatch on its banner counts the time since
 * it came; the next scan takes the banner away until its own answer is in.
 */

export type HubReceivingState = RouterOutputs['hubReceiving']['State']
type HubLine = HubReceivingState['lines'][number]
type HubShelved = HubReceivingState['extra'][number]
type Counts = HubReceivingState['counts']
export type HubScanAnswer = RouterOutputs['hubReceiving']['Scan']
export type HubScanRequest = RouterInputs['hubReceiving']['Scan']
export type HubCloseAnswer = RouterOutputs['hubReceiving']['Close']
type HubShelfReason = HubShelved['reasons'][number]

/** An answer the server keeps no list of — a repeat or a refusal — kept for this pass. */
type JournalEntry = {
	id: number
	answer: Extract<HubScanAnswer, { outcome: 'duplicate_scan' | 'refused' }>
}

const loadViaTrpc = (manifestId: string) =>
	trpcClient.hubReceiving.State.query({ manifestId })
const scanViaTrpc = (input: HubScanRequest) =>
	trpcClient.hubReceiving.Scan.mutate(input)
const closeViaTrpc = (manifestId: string) =>
	trpcClient.hubReceiving.Close.mutate({ manifestId })

const failure = (error: unknown) =>
	error instanceof Error ? error.message : String(error)


export interface HubReceivingProps {
	manifestId: string
	onBack: () => void
	/** Test seam: the state read. Defaults to the real tRPC client. */
	load?: (manifestId: string) => Promise<HubReceivingState>
	/** Test seam: one scan. Defaults to the real tRPC client. */
	scan?: (input: HubScanRequest) => Promise<HubScanAnswer>
	/** Test seam: the close. Defaults to the real tRPC client. */
	close?: (manifestId: string) => Promise<HubCloseAnswer>
	/** Test seams: the Box calls. Default to the real tRPC client. */
	boxes?: HubBoxSeams
	/** Test seams: «Этикетка не читается» — the search in the batch, the label, the print (ticket 48). */
	unreadable?: UnreadableLabelSeams
}

export function HubReceiving({
	manifestId,
	onBack,
	load = loadViaTrpc,
	scan = scanViaTrpc,
	close = closeViaTrpc,
	boxes = {},
	unreadable = {},
}: HubReceivingProps) {
	const { t } = useTranslate()
	const calls = { ...boxCallsViaTrpc, ...boxes }
	const refusalWords = useBoxRefusalWords()
	const answerWords = useAnswerWords()
	// A scanned Box waiting for its act; the opened Box whose Parcels are scanned now;
	// the last Box answer.
	const [pendingBox, setPendingBox] = useState<{ box: HubBoxCard; byBoxes: boolean } | null>(null)
	const [currentBoxCode, setCurrentBoxCode] = useState<string | null>(null)
	const [boxBanner, setBoxBannerState] = useState<BoxBanner | null>(null)
	const [boxBusy, setBoxBusy] = useState(false)
	const [identifying, setIdentifying] = useState(false)
	const [view, setView] = useState<HubReceivingState | null>(null)
	const [loadError, setLoadError] = useState<string | null>(null)
	const [sendError, setSendError] = useState<string | null>(null)
	const [answer, setAnswer] = useState<HubScanAnswer | null>(null)
	// When the banner shown now came — its stopwatch counts from here.
	const [shownAt, setShownAt] = useState(0)
	const [boxFilter, setBoxFilter] = useState<string | null>(null)
	const [journal, setJournal] = useState<JournalEntry[]>([])
	const [closeError, setCloseError] = useState<string | null>(null)
	// Bumped after each answer, so the scan field remounts and takes the cursor back.
	const [scanRound, setScanRound] = useState(0)
	const reads = useRef(0)
	const journalId = useRef(0)

	// The checklist is the server's: re-read after every act rather than rebuilt
	// here. Only the latest read lands; a failed re-read keeps what was shown.
	const refresh = useCallback(() => {
		const read = ++reads.current
		load(manifestId).then(
			(next) => {
				if (read !== reads.current) return
				setView(next)
				setLoadError(null)
			},
			(error) => {
				if (read === reads.current) setLoadError(failure(error))
			},
		)
	}, [load, manifestId])

	useEffect(() => {
		refresh()
	}, [refresh])

	// A Box answer replaces the last Parcel answer: once the banner goes with the next
	// scan, an older answer must not come back as if it were that scan's.
	const setBoxBanner = (banner: BoxBanner | null) => {
		setBoxBannerState(banner)
		if (!banner) return
		setAnswer(null)
		setShownAt(Date.now())
		playTone(banner.tone)
	}

	const showAnswer = (res: HubScanAnswer) => {
		setAnswer(res)
		setShownAt(Date.now())
		playTone(res.tone)
	}

	const withCounts = (counts: Counts) =>
		setView((prev) => (prev ? { ...prev, counts } : prev))

	const currentBox =
		view?.boxes.find((b) => b.code === currentBoxCode && b.state === 'opened' && b.containerId) ?? null

	// The scan itself: a Parcel is taken in by it, never weighed (ADR-0047).
	const send = async (code: string) => {
		setSendError(null)
		try {
			const res = await scan({
				manifestId,
				code,
				...(currentBox?.containerId ? { boxId: currentBox.containerId } : {}),
			})
			showAnswer(res)
			withCounts(res.counts)
			if (res.outcome === 'duplicate_scan' || res.outcome === 'refused') {
				const id = journalId.current++
				setJournal((prev) => [{ id, answer: res }, ...prev].slice(0, 50))
			}
			refresh()
		} catch (error) {
			// Nothing came back: the worker scans it again.
			setSendError(failure(error))
		}
	}

	// Every scan is identified first: a Box gets its own form; a Parcel the scan would
	// refuse is answered before anything is written.

	const onScan = async (code: string) => {
		setIdentifying(true)
		// The banner and the error were the previous scan's.
		setBoxBanner(null)
		setAnswer(null)
		setSendError(null)
		try {
			const seen = await calls.identify({
				manifestId,
				code,
				...(currentBox?.containerId ? { boxId: currentBox.containerId } : {}),
			})
			if (seen.kind === 'box') {
				setPendingBox({ box: seen.box, byBoxes: seen.byBoxes })
				return
			}
			if (seen.refusal && view) {
				const refusal: HubScanAnswer = {
					outcome: 'refused',
					tone: 'refused',
					reason: seen.refusal,
					trackingCode: code,
					...(seen.boxCode ? { boxCode: seen.boxCode } : {}),
					counts: view.counts,
				}
				showAnswer(refusal)
				const id = journalId.current++
				setJournal((prev) => [{ id, answer: refusal }, ...prev].slice(0, 50))
				return
			}
			await send(code)
		} catch {
			// Nothing read: the scan answers for itself.
			await send(code)
		} finally {
			setIdentifying(false)
			// The scan field remounts with the cursor for the next code.
			setScanRound((n) => n + 1)
		}
	}

	const boxAct = async (act: () => Promise<void>) => {
		if (boxBusy) return
		setBoxBusy(true)
		try {
			await act()
		} catch (error) {
			setBoxBanner({ tone: 'refused', outcome: 'error', title: t('hubBox.failed'), detail: boxFailure(error) })
		} finally {
			setBoxBusy(false)
			refresh()
		}
	}

	const doneWithBoxForm = () => {
		setPendingBox(null)
		setScanRound((n) => n + 1)
	}

	/** The words of a Box's weighing in a banner: its weight, and when beyond the tolerance, why. */
	const weighingWords = (box: HubBoxCard) =>
		[
			box.weightKg !== null ? t('hubBox.weighed', { weight: String(box.weightKg) }) : t('hubBox.notWeighed'),
			box.weightOutOfTolerance
				? t('hubBox.flaggedDetail', { declared: String(box.declaredWeightKg ?? '—') })
				: '',
		].filter(Boolean)

	const receiveWhole = (box: HubBoxCard, input: BoxWeighing) =>
		boxAct(async () => {
			const res = await calls.receiveBox({ manifestId, code: box.code, ...input })
			withCounts(res.counts)
			if (res.outcome === 'refused') {
				setBoxBanner(refusalWords(res))
				return
			}
			setBoxBanner(
				res.outcome === 'box_received'
					? {
							tone: res.tone,
							outcome: res.outcome,
							title: t('hubBox.receivedTitle', {
								code: res.box.code,
								point: res.box.pointCode ?? t('hubBox.mix'),
							}),
							detail: [
								t('hubBox.receivedDetail', { count: String(res.parcelCount) }),
								...weighingWords(res.box),
							].join(' · '),
						}
					: {
							tone: 'attention',
							outcome: res.outcome,
							title: t('hubBox.alreadyTitle', { code: res.box.code }),
							detail: t('hubBox.already_received'),
						},
			)
			doneWithBoxForm()
		})

	// Opening is one press after the scales (ticket 55): no bench step before it.
	const openBox = (box: HubBoxCard, input: BoxWeighing) =>
		boxAct(async () => {
			const res = await calls.openBox({ manifestId, code: box.code, ...input })
			withCounts(res.counts)
			if (res.outcome === 'refused') {
				setBoxBanner(refusalWords(res))
				return
			}
			setPendingBox(null)
			setCurrentBoxCode(res.box.code)
			setBoxFilter(res.box.code)
			setBoxBanner({
				tone: res.tone,
				outcome: res.outcome,
				title: t('hubBox.openedTitle', { code: res.box.code }),
				detail: [...weighingWords(res.box), t('hubBox.openedDetail')].join(' · '),
			})
			setScanRound((n) => n + 1)
		})

	const finishCurrent = (box: HubBoxCard) =>
		boxAct(async () => {
			if (!box.containerId) return
			const res = await calls.finishBox({ manifestId, containerId: box.containerId })
			withCounts(res.counts)
			if (res.outcome === 'refused') {
				setBoxBanner(refusalWords(res))
				return
			}
			setCurrentBoxCode(null)
			setBoxBanner({
				tone: res.tone,
				outcome: res.outcome,
				title: t('hubBox.finishedTitle', { code: res.box.code }),
				detail:
					res.shortage.length > 0
						? t('hubBox.finishedShort', { count: String(res.shortage.length) })
						: t('hubBox.finishedDetail'),
				shortage: res.shortage.map((x) => x.trackingCode),
				extras: res.extras.map((x) => x.trackingCode),
			})
		})

	const onClose = async () => {
		setCloseError(null)
		try {
			const res = await close(manifestId)
			setView((prev) =>
				prev ? { ...prev, state: res.state, closedAt: res.closedAt, counts: res.counts } : prev,
			)
		} catch (error) {
			setCloseError(failure(error))
		}
		refresh()
	}

	if (!view) {
		return (
			<div className='flex flex-col gap-3'>
				<BackButton onBack={onBack} />
				{loadError ? (
					<p className='text-sm text-destructive' data-testid='hub-receiving-load-error'>
						{t('hub.loadFailed', { message: loadError })}
					</p>
				) : (
					<p className='text-sm text-muted-foreground'>{t('hub.loading')}</p>
				)}
			</div>
		)
	}

	const closed = view.state === 'CLOSED'
	const expectedLines = view.lines.filter((l) => l.state === 'EXPECTED')
	const doneCount = view.lines.filter((l) => l.state === 'RECEIVED').length
	const hasSide =
		view.boxes.length + view.refused.length + view.extra.length + view.problems.length + journal.length > 0

	return (
		<div className='flex flex-col gap-6' data-testid='hub-receiving'>
			<div className='flex flex-col gap-3'>
				<BackButton onBack={onBack} />
				<div className='flex flex-wrap items-start justify-between gap-4'>
					<div className='flex flex-col gap-1'>
						<h2 className='flex flex-wrap items-center gap-2 text-xl font-semibold'>
							<span>{t('hub.receivingTitle', { dispatchRef: view.dispatchRef })}</span>
							<Badge
								variant={view.state === 'IN_RECEIVING' ? 'default' : 'secondary'}
								data-testid='hub-receiving-state'
								data-state={view.state}
							>
								{t(`manifestState.${view.state}`)}
							</Badge>
						</h2>
						<p className='text-sm text-muted-foreground'>
							{view.partnerName} · {t('hub.revision', { revision: String(view.revision) })}
						</p>
					</div>
					{!closed && (
						<CloseReceiving dispatchRef={view.dispatchRef} expected={expectedLines} onConfirm={onClose} />
					)}
				</div>
				<CountsLine counts={view.counts} done={doneCount} total={view.lines.length} />
			</div>

			{closeError && (
				<p role='alert' className='text-sm text-destructive' data-testid='hub-close-error'>
					{t('hub.closeFailed', { message: closeError })}
				</p>
			)}

			<section className='flex flex-col gap-3'>
				{!closed && currentBox && (
					<CurrentBoxBar
						box={currentBox}
						busy={boxBusy}
						onFinish={() => finishCurrent(currentBox)}
						onLeave={() => setCurrentBoxCode(null)}
					/>
				)}
				{closed ? (
					<p
						className='rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground'
						data-testid='hub-receiving-closed'
					>
						{t('hub.closedNotice')}
					</p>
				) : pendingBox ? (
					<BoxForm
						box={pendingBox.box}
						byBoxes={pendingBox.byBoxes}
						busy={boxBusy}
						onReceive={(input) => receiveWhole(pendingBox.box, input)}
						onOpen={(input) => openBox(pendingBox.box, input)}
						onCancel={doneWithBoxForm}
					/>
				) : (
					<>
						<div className='flex flex-wrap items-start gap-3'>
							<div className='min-w-[170px] flex-1'>
								<ScanInput key={scanRound} onScan={onScan} disabled={identifying} />
							</div>
							<UnreadableLabel
								manifestId={manifestId}
								onClosed={() => setScanRound((n) => n + 1)}
								{...unreadable}
							/>
						</div>
						<p className='-mt-1 text-xs text-muted-foreground'>
							{currentBox ? t('hubBox.scanHint', { code: currentBox.code }) : t('hub.scanHint')}
						</p>
						{sendError && (
							<p role='alert' className='text-sm text-destructive' data-testid='hub-scan-error'>
								{t('hub.scanFailed', { message: sendError })}
							</p>
						)}
					</>
				)}
				{boxBanner && <BoxAnswerBanner banner={boxBanner} since={shownAt} />}
				{answer && !boxBanner && (
					<ScanAnswerBanner
						answer={answer}
						words={answerWords(answer)}
						since={shownAt}
						actions={
							// Damage seen at the intake: recorded with a comment, and the
							// batch's Discrepancy «повреждение при приёме» (ticket 42).
							'parcelId' in answer ? (
								<DamageDialog
									key={answer.parcelId}
									parcelId={answer.parcelId}
									code={answer.trackingCode}
									testId='hub-damage-open'
									onClosed={() => setScanRound((n) => n + 1)}
								/>
							) : undefined
						}
					/>
				)}
			</section>

			<div className={cn('grid grid-cols-1 items-start gap-6', hasSide && 'lg:grid-cols-[minmax(0,1fr)_420px]')}>
				<Checklist lines={view.lines} box={boxFilter} onBox={setBoxFilter} />
				{hasSide && (
					<div className='flex flex-col gap-6'>
						<BoxesList
							boxes={view.boxes}
							selected={boxFilter}
							onSelect={setBoxFilter}
							onUnpack={(box) => {
								setCurrentBoxCode(box.code)
								setBoxFilter(box.code)
							}}
						/>
						<ShelvedList testId='hub-extra' title={t('hub.extraTitle')} items={view.extra} />
						<ShelvedList testId='hub-problems' title={t('hub.problemsTitle')} items={view.problems} />
						{journal.length > 0 && (
							<section className='flex flex-col gap-2'>
								<SectionTitle title={t('hub.journalTitle')} count={journal.length} />
								<ol className='flex flex-col gap-1' data-testid='hub-journal'>
									{journal.map((entry) => (
										<JournalRow key={entry.id} answer={entry.answer} />
									))}
								</ol>
							</section>
						)}
						<RefusedLinesList refused={view.refused} />
					</div>
				)}
			</div>
		</div>
	)
}

function BackButton({ onBack }: { onBack: () => void }) {
	const { t } = useTranslate()
	return (
		<Button variant='ghost' size='sm' className='-ml-2.5 w-fit text-muted-foreground' onClick={onBack}>
			<ArrowLeft />
			{t('hub.back')}
		</Button>
	)
}

function CountsLine({ counts, done, total }: { counts: Counts; done: number; total: number }) {
	const { t } = useTranslate()
	return (
		<div className='flex flex-wrap items-start gap-x-10 gap-y-3' data-testid='hub-counts'>
			<div
				className='flex min-w-60 flex-col gap-1'
				data-testid='hub-progress'
				data-done={done}
				data-total={total}
			>
				<span className='text-xs text-muted-foreground'>{t('hub.statReceived')}</span>
				<span className='text-lg font-semibold leading-tight tabular-nums'>
					<span data-testid='hub-count-received'>{counts.received}</span>{' '}
					<span className='font-normal text-muted-foreground' data-testid='hub-count-expected'>
						{t('hub.statOf', { count: String(counts.expected) })}
					</span>
				</span>
				<div className='mt-1 h-1.5 overflow-hidden rounded-full bg-muted'>
					<div
						className='h-full rounded-full bg-emerald-500 transition-all'
						style={{ width: `${total ? (done * 100) / total : 0}%` }}
					/>
				</div>
			</div>
			<Stat testId='hub-count-extra' label={t('hub.statExtra')} value={counts.extra} tone='warn' />
			<Stat testId='hub-count-problems' label={t('hub.statProblems')} value={counts.problems} tone='warn' />
			<Stat testId='hub-count-missing' label={t('hub.statMissing')} value={counts.missing} tone='bad' />
		</div>
	)
}

/** One count beside the progress: muted at zero, coloured when it needs a look. */
/** The shelf reasons, as words. */
function useReasons() {
	const { t } = useTranslate()
	return (reasons: readonly HubShelfReason[]) =>
		reasons.map((r) => t(`hubShelfReason.${r}`) || r).join(', ')
}

/** The words of one answer — its headline and its detail — by `outcome`. */
function useAnswerWords() {
	const { t } = useTranslate()
	const reasons = useReasons()
	return (answer: HubScanAnswer): { title: string; detail: string } => {
		switch (answer.outcome) {
			case 'received':
				return {
					title: t('hub.answerReceived', { point: answer.pointCode }),
					detail: t('hub.answerReceivedDetail', { code: answer.trackingCode }),
				}
			case 'received_not_from_box':
				return {
					title: t('hub.answerNotFromBox'),
					detail: answer.lineBoxCode
						? t('hub.answerNotFromBoxDetail', {
								code: answer.trackingCode,
								point: answer.pointCode,
								lineBox: answer.lineBoxCode,
								box: answer.boxCode,
							})
						: t('hub.answerNotFromBoxLooseDetail', {
								code: answer.trackingCode,
								point: answer.pointCode,
								box: answer.boxCode,
							}),
				}
			case 'to_hub_shelf':
				return {
					title: t('hub.answerToHubShelf'),
					detail: t('hub.answerToHubShelfDetail', {
						code: answer.trackingCode,
						reasons: reasons(answer.reasons),
					}),
				}
			case 'found':
				return {
					title: t('hubFound.title'),
					detail: t('hubFound.detail', { code: answer.trackingCode, partner: answer.partnerName, point: answer.pointCode }),
				}
			case 'duplicate_scan':
				return {
					title: t('hub.answerDuplicate'),
					detail: t('hub.answerDuplicateDetail', {
						code: answer.trackingCode,
						at: formatDayTime(answer.receivedAt),
					}),
				}
			case 'refused':
				return {
					title: t('hub.answerRefused'),
					detail:
						t('hub.answerRefusedDetail', {
							code: answer.trackingCode,
							reason: t(`hubScanRefusal.${answer.reason}`) || answer.reason,
						}) + (answer.boxCode ? ` — ${t('hubBox.inBoxNamed', { box: answer.boxCode })}` : ''),
				}
		}
	}
}

/** The answer to the last scan, large and in its colour. */
function ScanAnswerBanner({
	answer,
	words,
	actions,
	since,
}: {
	answer: HubScanAnswer
	words: { title: string; detail: string }
	actions?: ReactNode
	since?: number
}) {
	return (
		<ToneBanner
			tone={answer.tone}
			title={words.title}
			detail={words.detail}
			testId='hub-scan-answer'
			titleTestId='hub-scan-answer-title'
			detailTestId='hub-scan-answer-detail'
			attributes={{ 'data-outcome': answer.outcome }}
			actions={actions}
			since={since}
		/>
	)
}

function JournalRow({ answer }: { answer: JournalEntry['answer'] }) {
	const words = useAnswerWords()(answer)
	return (
		<li
			className={cn('rounded-md border px-3 py-1.5 text-sm', TONE_CLASS[answer.tone])}
			data-testid={`hub-journal-${answer.outcome}`}
			data-tone={answer.tone}
		>
			<span className='font-medium'>{words.title}</span> — {words.detail}
		</li>
	)
}

const LINE_STATES = ['EXPECTED', 'RECEIVED', 'MISSING_FROM_DELIVERY'] as const

/**
 * The checklist: everything the Manifest announced, ticked as it is taken in, crossed
 * when closed as not arrived. A search by code and a filter by state sit above it —
 * a batch can hold hundreds of lines — and the Box filter, set from the Boxes list.
 */
function Checklist({
	lines,
	box,
	onBox,
}: {
	lines: readonly HubLine[]
	box: string | null
	onBox: (code: string | null) => void
}) {
	const { t } = useTranslate()
	const reasons = useReasons()
	const [search, setSearch] = useState('')
	const [states, setStates] = useState<string[] | undefined>(undefined)
	const needle = search.trim().toLowerCase()
	const shown = useMemo(
		() =>
			lines.filter(
				(l) =>
					(!states?.length || states.includes(l.state)) &&
					(needle === '' || l.trackingCode.toLowerCase().includes(needle)) &&
					(box === null || l.box?.code === box),
			),
		[lines, states, needle, box],
	)
	const filtered = needle !== '' || !!states?.length || box !== null
	const reset = () => {
		setSearch('')
		setStates(undefined)
		onBox(null)
	}

	const columns = useMemo<ColumnDef<HubLine>[]>(
		() => [
			{
				id: 'mark',
				header: '',
				meta: { headClassName: 'w-8', cellClassName: 'w-8' },
				cell: ({ row: { original: line } }) =>
					line.state === 'RECEIVED' ? (
						<Check className='size-4 text-emerald-600' aria-label={t('manifestLineState.RECEIVED')} />
					) : line.state === 'MISSING_FROM_DELIVERY' ? (
						<X className='size-4 text-destructive' aria-label={t('manifestLineState.MISSING_FROM_DELIVERY')} />
					) : (
						<Circle className='size-4 text-muted-foreground/60' aria-label={t('manifestLineState.EXPECTED')} />
					),
			},
			{
				id: 'code',
				header: t('hub.shelfCode'),
				cell: ({ row: { original: line } }) => (
					<span
						className={cn(
							'font-mono',
							line.state === 'RECEIVED' && 'text-emerald-700 dark:text-emerald-400',
							line.state === 'MISSING_FROM_DELIVERY' && 'text-destructive',
						)}
					>
						{line.trackingCode}
					</span>
				),
			},
			{
				id: 'point',
				header: t('hub.shelfPoint'),
				cell: ({ row }) => row.original.pointCode ?? <span className='text-muted-foreground'>{t('hub.noPoint')}</span>,
			},
			{
				id: 'weight',
				// The partner's weight, as announced: a Parcel is never weighed at the hub (ADR-0047).
				header: t('hub.colWeight'),
				meta: { headClassName: 'text-right', cellClassName: 'text-right tabular-nums text-muted-foreground' },
				cell: ({ row }) => row.original.partnerWeightKg ?? '—',
			},
			{
				id: 'marks',
				header: '',
				cell: ({ row: { original: line } }) => (
					<div className='flex justify-end gap-1'>
						{line.toHubShelf.length > 0 && (
							<Badge
								variant='outline'
								className='border-amber-500/60 text-amber-700 dark:text-amber-400'
								title={reasons(line.toHubShelf)}
							>
								{t('hub.toShelfBadge')}
							</Badge>
						)}
						{line.state === 'MISSING_FROM_DELIVERY' && (
							<Badge variant='destructive'>{t('manifestLineState.MISSING_FROM_DELIVERY')}</Badge>
						)}
					</div>
				),
			},
		],
		[t, reasons],
	)

	const { table } = useDataTable({
		data: shown as HubLine[],
		columns,
		rowCount: shown.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		getRowId: (line) => line.lineId,
		initialState: { pagination: { pageIndex: 0, pageSize: 5000 } },
	})

	return (
		<section className='flex min-w-0 flex-col gap-2'>
			<SectionTitle
				title={t('hub.expectedTitle')}
				count={lines.length}
				aside={
					lines.length > 0 && (
						<>
							{box !== null && (
								<Button
									variant='secondary'
									size='sm'
									className='h-8 font-mono'
									data-testid='hub-lines-box-filter'
									aria-label={t('hub.boxFilterOff', { code: box })}
									onClick={() => onBox(null)}
								>
									<Package />
									{box}
									<X />
								</Button>
							)}
							<Input
								className='h-8 w-52'
								placeholder={t('hub.searchCode')}
								aria-label={t('hub.searchCode')}
								value={search}
								onChange={(e) => setSearch(e.target.value)}
							/>
							<FacetedFilter
								title={t('hub.colState')}
								values={states}
								setValues={setStates}
								options={LINE_STATES.map((state) => ({ value: state, label: t(`manifestLineState.${state}`) }))}
							/>
							{/* Always there, so the header does not jump; inactive while nothing is filtered. */}
							<Button
								variant='outline'
								size='icon-sm'
								className={cn('border-dashed', filtered && 'border-primary text-primary hover:text-primary')}
								data-testid='hub-lines-reset'
								aria-label={t('hub.resetFilters')}
								title={t('hub.resetFilters')}
								disabled={!filtered}
								onClick={reset}
							>
								<FilterX />
							</Button>
						</>
					)
				}
			/>
			<div data-testid='hub-lines'>
				<DataTable
					table={table}
					withPagination={false}
					maxHeightClassName='max-h-[640px]'
					emptyText={lines.length === 0 ? t('hub.linesEmpty') : undefined}
					row={{
						attributes: (line) => ({
							'data-testid': `hub-line-${line.lineId}`,
							'data-state': line.state,
							'data-to-hub-shelf': line.toHubShelf.length > 0 || undefined,
						}),
					}}
				/>
			</div>
		</section>
	)
}

/** One of the side lists — over the list, or on the hub shelf — each with its reasons. Nothing when empty. */
function ShelvedList({
	testId,
	title,
	items,
}: {
	testId: string
	title: string
	items: readonly HubShelved[]
}) {
	const { t } = useTranslate()
	const reasons = useReasons()
	if (items.length === 0) return null
	return (
		<section className='flex flex-col gap-2'>
			<SectionTitle title={title} count={items.length} />
			<CompactTable testId={testId}>
				<TableBody>
					{items.map((item) => (
						<TableRow key={item.parcelId} data-testid={`${testId}-${item.parcelId}`}>
							<TableCell>
								<div className='font-mono'>{item.trackingCode}</div>
								<div className='text-xs text-amber-700 dark:text-amber-400'>{reasons(item.reasons)}</div>
							</TableCell>
							<TableCell className='text-right text-muted-foreground'>
								{item.pointCode ?? t('hub.noPoint')}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</CompactTable>
		</section>
	)
}

/** How many lines the close confirmation names before it says "and N more". */
const CLOSE_LIST_LIMIT = 15

/**
 * «Закрыть приёмку» — final (ADR-0004). The confirmation says what closes as
 * «не прибыла»: exactly the lines still expected (the contract's promise; no
 * separate preview is asked of the server).
 */
function CloseReceiving({
	dispatchRef,
	expected,
	onConfirm,
}: {
	dispatchRef: string
	expected: readonly HubLine[]
	onConfirm: () => void
}) {
	const { t } = useTranslate()
	const shown = expected.slice(0, CLOSE_LIST_LIMIT)
	return (
		<AlertDialog>
			<AlertDialogTrigger asChild>
				<Button variant='destructive' data-testid='hub-close'>
					{t('hub.close')}
				</Button>
			</AlertDialogTrigger>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{t('hub.closeConfirmTitle', { dispatchRef })}</AlertDialogTitle>
					<AlertDialogDescription data-testid='hub-close-summary' data-count={expected.length}>
						{expected.length > 0
							? t('hub.closeConfirmBody', { count: String(expected.length) })
							: t('hub.closeConfirmNone')}
					</AlertDialogDescription>
					{shown.length > 0 && (
						<ul className='flex flex-col gap-0.5 text-sm' data-testid='hub-close-missing'>
							{shown.map((line) => (
								<li key={line.lineId} className='font-mono'>
									{line.trackingCode}
								</li>
							))}
							{expected.length > shown.length && (
								<li className='text-muted-foreground'>
									{t('hub.closeConfirmMore', {
										count: String(expected.length - shown.length),
									})}
								</li>
							)}
						</ul>
					)}
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel>{t('hub.cancel')}</AlertDialogCancel>
					<AlertDialogAction variant='destructive' data-testid='hub-close-confirm' onClick={onConfirm}>
						{t('hub.closeConfirm')}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}
